/**
 * PWA glue for the web build: registers the service worker and, when a new
 * deploy has downloaded, offers a reload instead of swapping the game out
 * mid-session. Skipped on the dev server (Vite serves modules live) and
 * inside the native Android app (Capacitor ships its own copy of the build).
 */

import { Capacitor } from '@capacitor/core';

export function initPwa(onUpdateReady: (apply: () => void) => void): void {
  if (import.meta.env.DEV || Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) return;

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  void navigator.serviceWorker.register('/sw.js').then((reg) => {
    const offer = (worker: ServiceWorker) => onUpdateReady(() => worker.postMessage('skipWaiting'));
    // A new version already waiting (downloaded on an earlier visit).
    if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
    reg.addEventListener('updatefound', () => {
      const next = reg.installing;
      if (!next) return;
      next.addEventListener('statechange', () => {
        // Only an update, not the very first install, needs a prompt.
        if (next.state === 'installed' && navigator.serviceWorker.controller) offer(next);
      });
    });
    // Look for a new deploy whenever the player comes back to the game.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) void reg.update();
    });
  });
}
